import React, { useEffect } from 'react';
import { Form, Input, Select, Checkbox } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { useCustomFieldDefinitions } from 'src/hooks/useCustomFields';
import {
  LabeledCustomNumberInput,
  numberFieldError,
} from 'src/components/CustomNumberInput';

type Props = {
  category: string;
  /** RHF path prefix, e.g. "properties" or "local.properties" */
  propertiesPath?: string;
  setModified?: (modified: boolean) => void;
};

/**
 * Renders editors for custom fields of a given category.
 * Integrates with react-hook-form via FormProvider.
 */
export function CustomFieldEditor({
  category,
  propertiesPath = 'properties',
  setModified,
}: Props) {
  const projectId = useSelector((state: any) => state.mainFrame.projectId);
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );
  const { data: allDefs } = useCustomFieldDefinitions(projectId);
  const fields = allDefs?.[category] ?? [];
  const { control, setValue, getValues, setError, clearErrors, watch } =
    useFormContext();

  const fieldPath = (fieldId: string) =>
    `${propertiesPath}.custom_fields.${fieldId}`;

  // Keep RHF errors in sync so Apply is disabled for out-of-range values,
  // including values already present when the panel opens.
  useEffect(() => {
    fields.forEach((field: any) => {
      if (field.kind !== 'number') return;
      const path = fieldPath(field.id);
      const value =
        getValues(path) !== undefined ? getValues(path) : field.default;
      const err = numberFieldError(value, field);
      if (err) {
        setError(path, { type: 'validate', message: err });
      } else {
        clearErrors(path);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, propertiesPath]);

  if (fields.length === 0) return null;

  const markModified = () => {
    if (setModified) setModified(true);
  };

  return (
    <>
      {fields.map((field: any) => {
        const path = fieldPath(field.id);
        const defaultVal = field.default;
        const watched = watch(path);
        const currentValue =
          watched !== undefined
            ? watched
            : getValues(path) !== undefined
              ? getValues(path)
              : defaultVal;
        const numberError =
          field.kind === 'number'
            ? numberFieldError(currentValue, field)
            : null;

        if (field.kind === 'number') {
          return (
            <Controller
              key={field.id}
              control={control}
              name={path}
              defaultValue={
                getValues(path) !== undefined ? getValues(path) : defaultVal
              }
              rules={{
                validate: (v) => numberFieldError(v, field) || true,
              }}
              render={({ field: { onChange, value } }) => (
                <LabeledCustomNumberInput
                  label={field.name}
                  value={value}
                  precision={field.precision ?? 0}
                  min={field.min}
                  max={field.max}
                  disabled={!permissionWrite}
                  error={numberError}
                  onChange={(next) => {
                    onChange(next);
                    markModified();
                    const err = numberFieldError(next, field);
                    if (err) {
                      setError(path, { type: 'validate', message: err });
                    } else {
                      clearErrors(path);
                    }
                  }}
                />
              )}
            />
          );
        }

        return (
          <Form.Field key={field.id}>
            <label>{field.name}</label>
            {field.kind === 'text' && (
              <Controller
                control={control}
                name={path}
                defaultValue={
                  getValues(path) !== undefined ? getValues(path) : defaultVal
                }
                render={({ field: { ref, onChange, value, ...rest } }) => (
                  <Input
                    {...rest}
                    value={value ?? ''}
                    disabled={!permissionWrite}
                    onChange={(e, { value: v }) => {
                      onChange(v);
                      markModified();
                    }}
                  />
                )}
              />
            )}
            {field.kind === 'choice' && (
              <Controller
                control={control}
                name={path}
                defaultValue={
                  getValues(path) !== undefined ? getValues(path) : defaultVal
                }
                render={({ field: { ref, onChange, value, ...rest } }) => (
                  <Select
                    {...rest}
                    options={(field.choices ?? []).map((c: string) => ({
                      key: c,
                      value: c,
                      text: c,
                    }))}
                    value={value ?? ''}
                    disabled={!permissionWrite}
                    onChange={(_, d) => {
                      onChange(d.value);
                      markModified();
                    }}
                  />
                )}
              />
            )}
            {field.kind === 'list' && (
              <Controller
                control={control}
                name={path}
                defaultValue={
                  getValues(path) !== undefined
                    ? getValues(path)
                    : defaultVal ?? []
                }
                render={({ field: { ref, onChange, value, ...rest } }) => (
                  <Select
                    {...rest}
                    multiple
                    options={(field.choices ?? []).map((c: string) => ({
                      key: c,
                      value: c,
                      text: c,
                    }))}
                    value={value ?? []}
                    disabled={!permissionWrite}
                    onChange={(_, d) => {
                      onChange(d.value);
                      markModified();
                    }}
                  />
                )}
              />
            )}
            {field.kind === 'boolean' && (
              <Controller
                control={control}
                name={path}
                defaultValue={
                  getValues(path) !== undefined ? getValues(path) : defaultVal
                }
                render={({ field: { ref, onChange, value } }) => (
                  <Checkbox
                    checked={!!value}
                    disabled={!permissionWrite}
                    onChange={(_, d) => {
                      onChange(!!d.checked);
                      // ensure nested path exists for submit
                      setValue(path, !!d.checked);
                      markModified();
                    }}
                  />
                )}
              />
            )}
          </Form.Field>
        );
      })}
    </>
  );
}
